/**
 * Safe localStorage / sessionStorage.
 *
 * `localStorage.setItem()` THROWS in two situations users really hit:
 *   * the phone's storage is full (QuotaExceededError)
 *   * Safari private browsing, which throws on every single write
 *
 * The login path did this unguarded:
 *
 *     localStorage.setItem("token", res.data.token);   // <- throws
 *     setToken(res.data.token);                        // <- never runs
 *     setUser(res.data.user);                          // <- never runs
 *
 * ...so a full phone or a private tab meant the user simply could not log in,
 * with no error explaining why. The session was valid; we just crashed before
 * using it.
 *
 * These helpers never throw. A failed write is reported (so callers can warn if
 * they care) but the app keeps running - a lost "remember me" is a nuisance; a
 * login that dies is a broken app.
 */

/**
 * Tell this tab that a localStorage key changed.
 *
 * The browser's own `storage` event fires ONLY in other tabs, never in the one
 * that wrote - so anything watching for a preference change in the same tab
 * hears nothing. `useProfileDiff` listened for `xelanti:settings-changed` and
 * NOTHING in the codebase ever dispatched it, so the "changed from your saved
 * profile" dots only refreshed when the Settings page happened to re-render for
 * its own reasons. Toggling All caps re-rendered and the dot appeared; changing
 * the aisle order or comparable stores on another page did not, and the dots
 * went on showing a stale answer in both directions. (v17.06)
 *
 * Session storage is deliberately excluded: it holds transient UI state, not
 * preferences, and nothing diffs it.
 */
function announceWrite(key, session) {
  if (session) return;
  try {
    if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
    const Ctor = typeof CustomEvent === "function" ? CustomEvent : null;
    window.dispatchEvent(
      Ctor ? new Ctor("xelanti:settings-changed", { detail: { key } })
           : Object.assign(new Event("xelanti:settings-changed"), { detail: { key } })
    );
  } catch {
    // A listener that throws must never break the write that already succeeded.
  }
}

/** Write a value. Returns true if it stuck, false if storage refused it. */
export function safeSet(key, value, session = false) {
  try {
    const store = session ? sessionStorage : localStorage;
    store.setItem(key, value);
    announceWrite(key, session);
    return true;
  } catch (e) {
    // Full disk, or Safari private mode. Never let this kill the caller.
    console.warn(`[storage] could not save "${key}":`, e && e.name);
    return false;
  }
}

/** Read a value, or `fallback` if it's missing or storage is unavailable. */
export function safeGet(key, fallback = null, session = false) {
  try {
    const store = session ? sessionStorage : localStorage;
    const v = store.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

/** Remove a value. Never throws. */
export function safeRemove(key, session = false) {
  try {
    (session ? sessionStorage : localStorage).removeItem(key);
    // Clearing a preference is a change like any other: setComparableStores([])
    // removes the key to mean "all stores", and that must move the dot too.
    announceWrite(key, session);
    return true;
  } catch {
    return false;
  }
}

/** Read + JSON.parse, returning `fallback` on anything unexpected. */
export function safeGetJSON(key, fallback = null, session = false) {
  const raw = safeGet(key, null, session);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;   // corrupted entry - don't crash, just ignore it
  }
}

/** JSON.stringify + write. Returns false if storage refused it. */
export function safeSetJSON(key, value, session = false) {
  try {
    return safeSet(key, JSON.stringify(value), session);
  } catch {
    return false;      // value wasn't serialisable
  }
}

/** True when storage is actually usable (false in Safari private browsing). */
export function storageAvailable(session = false) {
  try {
    const store = session ? sessionStorage : localStorage;
    const probe = "__probe__";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}
